import type { QueryClient } from '@tanstack/react-query';
import type { CreativePerformanceParams } from '@customTypes/CreativePerformance';
import type { LeadAttributionParams } from '@customTypes/LeadAttribution';
import type { PerformanceParams } from '@customTypes/PerformanceCommand';
import type { DashboardAttentionSurface } from '@apis/FeedService';

export const preserveSameTenantPlaceholder = <T>(
  tenantKey: string,
  previousData: T | undefined,
  previousQueryKey: readonly unknown[] | undefined,
): T | undefined =>
  previousQueryKey?.[1] === tenantKey ? previousData : undefined;

/** Pure tenant-bound cache-key factories shared by data hooks and race tests. */
export const budgetAllocationQueryKeys = {
  all: ['budget-allocation'] as const,
  dashboard: (tenantKey: string) =>
    ['budget-allocation', 'v1', tenantKey] as const,
};

export const creativePerformanceQueryKeys = {
  all: ['creative-performance'] as const,
  view: (tenantKey: string, params?: CreativePerformanceParams) =>
    [
      'creative-performance',
      tenantKey,
      params?.start_date || 'default',
      params?.end_date || 'default',
      params?.conversion_event || 'auto',
    ] as const,
};

export const executiveOverviewQueryKeys = {
  all: ['executive-overview'] as const,
  dashboard: (tenantKey: string) =>
    ['executive-overview', 'v1', tenantKey] as const,
};

export const feedQueryKeys = {
  all: ['feed'] as const,
  tenant: (tenantKey: string) => ['feed', tenantKey] as const,
  status: (tenantKey: string, status: 'open' | 'archived') =>
    ['feed', tenantKey, 'list', status] as const,
  list: (
    tenantKey: string,
    status: 'open' | 'archived' = 'open',
    attentionSurface?: DashboardAttentionSurface,
  ) => ['feed', tenantKey, 'list', status, attentionSurface || 'all'] as const,
};

export const leadAttributionQueryKeys = {
  all: ['lead-attribution'] as const,
  dashboard: (tenantKey: string, params?: LeadAttributionParams) =>
    [
      'lead-attribution',
      tenantKey,
      params?.start_date || 'default',
      params?.end_date || 'default',
      params?.crm_provider || 'auto',
    ] as const,
  definition: (tenantKey: string) =>
    ['lead-attribution', tenantKey, 'lead-definition'] as const,
  wonDefinition: (tenantKey: string) =>
    ['lead-attribution', tenantKey, 'won-definition'] as const,
  metricsLayout: (tenantKey: string) =>
    ['lead-attribution', tenantKey, 'metrics-layout'] as const,
  stageMetrics: (tenantKey: string) =>
    ['lead-attribution', tenantKey, 'stage-metrics'] as const,
};

/**
 * The dashboard windows currently on screen for this tenant, derived from the active
 * `leadAttributionQueryKeys.dashboard` cache entries. A definition change makes each one a new
 * page, and a warming page only polls reads that never start a build, so the server starts one
 * for each of these. Keeps the dashboard-key tuple shape known in exactly one place.
 */
export const shownLeadAttributionWindows = (
  queryClient: QueryClient,
  tenantKey: string,
): LeadAttributionParams[] =>
  queryClient
    .getQueryCache()
    .findAll({ queryKey: ['lead-attribution', tenantKey], type: 'active' })
    .map((entry) => entry.queryKey)
    .filter((key) => key.length === 5)
    .map(([, , start, end, crm]) => ({
      ...(start !== 'default' ? { start_date: String(start) } : {}),
      ...(end !== 'default' ? { end_date: String(end) } : {}),
      ...(crm !== 'auto'
        ? { crm_provider: crm as LeadAttributionParams['crm_provider'] }
        : {}),
    }));

export const monitorQueryKeys = {
  all: ['monitors'] as const,
  readings: (tenantKey: string) => ['monitors', 'readings', tenantKey] as const,
};

export const objectiveFeedQueryKeys = {
  all: ['objectiveFeed'] as const,
  tenant: (tenantKey: string) => ['objectiveFeed', tenantKey] as const,
  list: (tenantKey: string) => ['objectiveFeed', tenantKey, 'list'] as const,
};

export const performanceQueryKeys = {
  all: ['performance'] as const,
  dashboard: (tenantKey: string, params?: PerformanceParams) =>
    [
      'performance',
      tenantKey,
      params?.start_date || 'default',
      params?.end_date || 'default',
      params?.attribution || 'blended',
    ] as const,
  health: (tenantKey: string) =>
    ['performance', 'connector-health', tenantKey] as const,
};

export const scheduledWorkQueryKeys = {
  all: ['scheduledWork'] as const,
  tenant: (tenantKey: string) => ['scheduledWork', tenantKey] as const,
  list: (tenantKey: string) => ['scheduledWork', tenantKey, 'list'] as const,
  detail: (tenantKey: string, id: string) =>
    ['scheduledWork', tenantKey, 'detail', id] as const,
  calendars: (tenantKey: string) =>
    ['scheduledWork', tenantKey, 'calendar'] as const,
  calendar: (tenantKey: string, startDate: string, endDate: string) =>
    [
      ...scheduledWorkQueryKeys.calendars(tenantKey),
      startDate,
      endDate,
    ] as const,
  executions: (tenantKey: string, scheduleId: string) =>
    ['scheduledWork', tenantKey, 'executions', scheduleId] as const,
};

export const dataQueryKeys = {
  all: ['data'] as const,
  analytics: (tenantKey: string) => ['data', tenantKey, 'analytics'] as const,
  render: (tenantKey: string, markup: string) => ['data', tenantKey, 'render', markup] as const,
  templates: (tenantKey: string) => ['data', tenantKey, 'templates'] as const,
  templatePreview: (tenantKey: string, id: string, days?: number) => ['data', tenantKey, 'template-preview', id, days ?? null] as const,
};

export const workBoardQueryKeys = {
  all: ['workBoard'] as const,
  current: (tenantKey: string) => ['workBoard', tenantKey] as const,
};

export const workBoardDeliveredQueryKeys = {
  all: ['workBoardDelivered'] as const,
  tenant: (tenantKey: string) => ['workBoardDelivered', tenantKey] as const,
  range: (tenantKey: string, since: string, until: string) =>
    ['workBoardDelivered', tenantKey, since, until] as const,
};

export const responseAnnotationQueryKeys = {
  all: ['response-annotations'] as const,
  conversation: (tenantKey: string, conversationId: string) =>
    [...responseAnnotationQueryKeys.all, tenantKey, conversationId] as const,
};
